import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Play, Pause, Loader2, Flame } from "lucide-react";
import { api } from "@/lib/api";

const FOUNDERS_VIDEO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/8o7z2jxa_gemini_generated_video_5e1d8fa2.mp4";

const T = {
  fr: {
    eyebrow: "Le mot au coin du feu",
    title: "Un mot du fondateur",
    listen: "Les murmures du Cocoon",
    resume: "Reprendre",
    pause: "Mettre en pause",
    loading: "On allume la voix…",
    note: "La voix du fondateur",
    tagline: "Réveiller le désir d'apprendre.",
    signature: "Les Fondateurs de Cocoon Mind",
    letter: [
      "Chère communauté,",
      "Si vous repensez à vos plus beaux souvenirs d'apprentissage, ils ont rarement eu lieu le nez plongé dans un manuel scolaire, sous la menace d'une mauvaise note ou lors d'un exercice de récitation par cœur.",
      "Vos plus grands apprentissages sont nés d'une étincelle.",
      "Un sujet qui vous a soudainement captivé, une question fascinante posée au bon moment, une discussion passionnée qui a changé votre regard sur le monde. Bref : ce moment magique où le devoir s'est métamorphosé en désir.",
      "Pourtant, le système éducatif traditionnel continue de mesurer l'intelligence par des points, des classements et des programmes standardisés. À force de vouloir faire entrer tout le monde dans le même moule, il réussit souvent une chose : éteindre la curiosité naturelle.",
      "C'est pour cela que nous avons créé Cocoon Mind.",
      "Nous n'avons pas imaginé Cocoon Mind pour remplacer l'école ou pour distribuer encore plus de devoirs. Nous l'avons pensé comme un précepteur numérique d'inspiration.",
      "Un compagnon d'apprentissage qui ne vous juge pas, ne vous évalue pas, mais apprend à vous connaître. Son unique mission ? Allumer la flamme.",
      "Inspirer, en reliant vos passions du quotidien aux plus grandes connaissances humaines.",
      "Faire aspirer, en posant les questions qui éveillent l'esprit et élargissent les horizons.",
      "Révéler des vocations, en transformant une simple intuition en un véritable élan de vie.",
      "Nous croyons profondément qu'apprendre ne devrait jamais être une corvée austère, mais une aventure personnelle, vivante et exaltante.",
      "Cocoon Mind est cet espace bienveillant — ce cocon — où la curiosité retrouve enfin sa liberté.",
      "Bienvenue dans l'aventure,",
    ],
  },
  en: {
    eyebrow: "A word by the campfire",
    title: "A word from the founder",
    listen: "The whispers of the Cocoon",
    resume: "Resume",
    pause: "Pause",
    loading: "Lighting the voice…",
    note: "The founder's voice",
    tagline: "Awakening the desire to learn.",
    signature: "The Founders of Cocoon Mind",
    letter: [
      "Dear community,",
      "If you think back to your fondest memories of learning, they rarely happened with your nose buried in a textbook, under the threat of a bad grade or reciting by heart.",
      "Your greatest learning was born from a spark.",
      "A subject that suddenly captivated you, a fascinating question asked at the right moment, a passionate discussion that changed the way you see the world. In short: that magical moment when duty turned into desire.",
      "Yet the traditional education system keeps measuring intelligence with points, rankings and standardised programmes. In trying to fit everyone into the same mould, it often succeeds at one thing: putting out natural curiosity.",
      "That is why we created Cocoon Mind.",
      "We did not imagine Cocoon Mind to replace school or to hand out even more homework. We conceived it as a digital tutor of inspiration.",
      "A learning companion that does not judge you, does not grade you, but gets to know you. Its only mission? To light the flame.",
      "To inspire, by connecting your everyday passions to the greatest human knowledge.",
      "To make you aspire, by asking the questions that awaken the mind and widen horizons.",
      "To reveal vocations, by turning a simple intuition into a true impulse for life.",
      "We deeply believe that learning should never be a bleak chore, but a personal, living and exhilarating adventure.",
      "Cocoon Mind is that caring space — this cocoon — where curiosity finally regains its freedom.",
      "Welcome to the adventure,",
    ],
  },
  es: {
    eyebrow: "Unas palabras al calor del fuego",
    title: "Unas palabras del fundador",
    listen: "Los susurros del Cocoon",
    resume: "Reanudar",
    pause: "Pausar",
    loading: "Encendiendo la voz…",
    note: "La voz del fundador",
    tagline: "Despertar el deseo de aprender.",
    signature: "Los Fundadores de Cocoon Mind",
    letter: [
      "Querida comunidad,",
      "Si recuerdas tus mejores momentos de aprendizaje, rara vez ocurrieron con la nariz metida en un libro de texto, bajo la amenaza de una mala nota o recitando de memoria.",
      "Tus mayores aprendizajes nacieron de una chispa.",
      "Un tema que de repente te cautivó, una pregunta fascinante hecha en el momento justo, una conversación apasionada que cambió tu mirada sobre el mundo. En resumen: ese momento mágico en que el deber se transformó en deseo.",
      "Sin embargo, el sistema educativo tradicional sigue midiendo la inteligencia con puntos, clasificaciones y programas estandarizados. A fuerza de querer meter a todos en el mismo molde, suele lograr una cosa: apagar la curiosidad natural.",
      "Por eso creamos Cocoon Mind.",
      "No imaginamos Cocoon Mind para reemplazar la escuela ni para repartir aún más tareas. Lo concebimos como un preceptor digital de inspiración.",
      "Un compañero de aprendizaje que no te juzga, no te evalúa, pero aprende a conocerte. ¿Su única misión? Encender la llama.",
      "Inspirar, conectando tus pasiones cotidianas con los mayores conocimientos humanos.",
      "Hacer aspirar, planteando las preguntas que despiertan la mente y amplían los horizontes.",
      "Revelar vocaciones, transformando una simple intuición en un verdadero impulso de vida.",
      "Creemos profundamente que aprender nunca debería ser una tarea austera, sino una aventura personal, viva y apasionante.",
      "Cocoon Mind es ese espacio acogedor — este capullo — donde la curiosidad recupera por fin su libertad.",
      "Bienvenido a la aventura,",
    ],
  },
  nl: {
    eyebrow: "Een woord bij het kampvuur",
    title: "Een woord van de oprichter",
    listen: "De fluisteringen van de Cocoon",
    resume: "Hervatten",
    pause: "Pauzeren",
    loading: "De stem aansteken…",
    note: "De stem van de oprichter",
    tagline: "Het verlangen om te leren wakker maken.",
    signature: "De oprichters van Cocoon Mind",
    letter: [
      "Beste gemeenschap,",
      "Als je terugdenkt aan je mooiste leerherinneringen, gebeurden die zelden met je neus in een schoolboek, onder dreiging van een slecht cijfer of tijdens het uit het hoofd opzeggen.",
      "Je grootste leermomenten ontstonden uit een vonk.",
      "Een onderwerp dat je plots boeide, een fascinerende vraag op het juiste moment, een gepassioneerd gesprek dat je kijk op de wereld veranderde. Kortom: dat magische moment waarop plicht veranderde in verlangen.",
      "Toch blijft het traditionele onderwijssysteem intelligentie meten met punten, ranglijsten en gestandaardiseerde programma's. Door iedereen in dezelfde mal te willen persen, slaagt het vaak in één ding: de natuurlijke nieuwsgierigheid doven.",
      "Daarom hebben we Cocoon Mind gemaakt.",
      "We bedachten Cocoon Mind niet om de school te vervangen of om nog meer huiswerk uit te delen. We zagen het als een digitale leermeester van inspiratie.",
      "Een leergezel die je niet beoordeelt, je niet evalueert, maar je leert kennen. Zijn enige missie? De vlam aansteken.",
      "Inspireren, door je dagelijkse passies te verbinden met de grootste menselijke kennis.",
      "Doen verlangen, door de vragen te stellen die de geest wekken en horizonten verruimen.",
      "Roepingen onthullen, door een eenvoudige intuïtie om te vormen tot een echte levensdrang.",
      "We geloven diep dat leren nooit een strenge karwei zou mogen zijn, maar een persoonlijk, levendig en opwindend avontuur.",
      "Cocoon Mind is die warme ruimte — deze cocon — waar nieuwsgierigheid eindelijk haar vrijheid terugvindt.",
      "Welkom in het avontuur,",
    ],
  },
};

export function FoundersWord() {
  const { i18n } = useTranslation();
  const L = T[(i18n.language || "fr").slice(0, 2)] || T.fr;
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const [videoOpacity, setVideoOpacity] = useState(0); // fondu

  const onVideoLoaded = () => setVideoOpacity(1); // fade-in doux
  const onVideoTime = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const remaining = v.duration - v.currentTime;
    const nearEdge = v.currentTime < 1 || remaining < 1; // fondu au noir en début/fin de boucle
    setVideoOpacity(nearEdge ? 0 : 1);
  };

  const toggle = async () => {
    if (state === "playing") { audioRef.current?.pause(); setState("paused"); return; }
    if (state === "paused" && audioRef.current) { audioRef.current.play(); setState("playing"); return; }
    setState("loading");
    try {
      const { data } = await api.get("/founders/audio", { params: { lang: (i18n.language || "fr").slice(0, 2) } });
      const audio = new Audio(`data:audio/mpeg;base64,${data.audio}`);
      audioRef.current = audio;
      audio.onended = () => setState("idle");
      audio.onpause = () => setState((s) => (s === "playing" ? "paused" : s));
      await audio.play();
      setState("playing");
    } catch {
      setState("idle");
      toast.error(L.loading);
    }
  };

  const playing = state === "playing";
  const label = state === "loading" ? L.loading : playing ? L.pause : state === "paused" ? L.resume : L.listen;

  return (
    <section className="max-w-6xl mx-auto px-6 py-14 sm:py-24" data-testid="founders-section">
      <div className="text-center mb-12">
        <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">
          <Flame size={14} /> {L.eyebrow}
        </p>
        <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl tracking-tight">{L.title}</h2>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Poste radio au coin du feu */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          <div className="relative">
            <video
              ref={videoRef}
              src={FOUNDERS_VIDEO}
              autoPlay loop muted playsInline
              preload="auto"
              onLoadedData={onVideoLoaded}
              onTimeUpdate={onVideoTime}
              data-testid="founders-video"
              style={{ opacity: videoOpacity, transition: "opacity 900ms ease-in-out" }}
              className={`w-full h-auto object-cover hero-video-fade ${
                playing ? "brightness-110" : "brightness-95"}`}
            />
            {/* égaliseur */}
            {playing && (
              <div className="absolute bottom-8 left-10 flex items-end gap-1.5" data-testid="founders-eq">
                {[0, 1, 2, 3, 4].map((i) => (
                  <span key={i} className="w-1.5 rounded-full bg-primary/80 founders-bar" style={{ animationDelay: `${i * 0.12}s` }} />
                ))}
              </div>
            )}
          </div>

          <div className="mt-7 flex flex-col items-center gap-3">
            <button
              onClick={toggle}
              disabled={state === "loading"}
              data-testid="founders-play-btn"
              className="btn-frost-orange inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-base disabled:opacity-60"
            >
              {state === "loading" ? <Loader2 size={18} className="animate-spin" /> : playing ? <Pause size={18} /> : <Play size={18} />}
              {label}
            </button>
            <span className="text-xs uppercase tracking-wider text-foreground/40">{L.note}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
